import React from 'react'
import { View, StyleSheet, Text } from '@react-pdf/renderer'
import { Trans, useTranslation } from 'react-i18next'

import * as theme from '../theme'
import { TAlarmReportProps, TAgentReportProps } from '../Provider'
import {
  createDirectionalStyles,
  stripBidi,
  normalizeArabicComma,
} from '../Title/CommonUtils'

import Loadable from 'src/utils/Loadable'
import { absurd } from 'fp-ts/lib/function'
import { TFunction } from 'i18next'
import { TGroupReference } from 'src/parser/entities/Alarm/Preview/Provider/useAlarmAssignedEntity'

const commonStyles = StyleSheet.create({
  root: {
    color: theme.colors.documentSubHeader,
    padding: theme.padding(2),
    display: 'flex',
    flexDirection: 'row',
  },
  column: {
    flex: 1,
    flexDirection: 'column',
  },
  fieldTitle: {
    ...theme.styles.subtitle,
    color: 'white',
  },
  fieldContent: {
    ...theme.styles.text,
    color: 'white',
  },
  alarmBackgroundColor: {
    backgroundColor: theme.colors.documentSubHeaderBg,
  },
  activityReportBackgroundColor: {
    backgroundColor: theme.colors.activityReportSubHeaderBg,
  },
})

type TFieldProps = {
  title: string
  children: React.ReactNode
  lang: string
}

const Field = ({ title, children, lang }: TFieldProps): JSX.Element => {
  const directionalStyles = createDirectionalStyles(commonStyles, lang)

  return (
    <View style={directionalStyles.container}>
      <Text
        style={[commonStyles.fieldTitle, directionalStyles.text]}
      >{`${title}: `}</Text>
      <Text style={[commonStyles.fieldContent]}>{children}</Text>
    </View>
  )
}

const Field2 = ({
  i18nKey,
  values,
}: {
  i18nKey: string
  values: Record<string, unknown>
}): JSX.Element => {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const directionalStyles = createDirectionalStyles(commonStyles, lang)

  return (
    <View style={directionalStyles.container}>
      <Trans
        i18nKey={i18nKey}
        values={values}
        components={{
          label: (
            <Text style={[commonStyles.fieldTitle, directionalStyles.text]} />
          ),
          value: (
            <Text style={[commonStyles.fieldContent, directionalStyles.text]} />
          ),
          error: (
            <Text
              style={[
                commonStyles.fieldContent,
                directionalStyles.text,
                { color: theme.colors.error },
              ]}
            />
          ),
        }}
      />
    </View>
  )
}

type CommonHeaderProps = {
  children: React.ReactNode
  customStyle: { paddingBottom: number } | object
  lang: string
}

const CommonHeader = ({
  children,
  customStyle = {},
  lang,
}: CommonHeaderProps): JSX.Element => {
  const directionalStyles = createDirectionalStyles(commonStyles, lang)

  return <View style={[directionalStyles.root, customStyle]}>{children}</View>
}

const renderField = (
  title: string,
  content: React.ReactNode,
  lang: string,
): JSX.Element => (
  <Field
    title={title}
    lang={lang}
  >
    {content}
  </Field>
)

export const AgentActivityHeader = ({
  entity,
  assignedPassport,
}: TAgentReportProps): JSX.Element | null => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  const assignedPassportData = Loadable.isSuccess(assignedPassport)
    ? assignedPassport.data
    : null

  return (
    <CommonHeader
      customStyle={{
        backgroundColor:
          commonStyles.activityReportBackgroundColor.backgroundColor,
      }}
      lang={lang}
    >
      <View style={commonStyles.column}>
        {renderField(t('agent_activity_report.header.id'), entity.id, lang)}
        {renderField(
          t('agent_activity_report.header.creation_date'),
          typeof entity.checkIn === 'object' &&
            normalizeArabicComma(
              stripBidi(
                t('alarm_report.logs.date_format', {
                  date: new Date(entity.checkIn?.timestamp),
                }),
              ),
            ),
          lang,
        )}
        {renderField(
          t('agent_activity_report.header.organisation'),
          assignedPassportData?.organization,
          lang,
        )}
      </View>
      <View style={commonStyles.column}>
        {renderField(
          t('agent_activity_report.header.first_name'),
          assignedPassportData?.firstname,
          lang,
        )}
        {renderField(
          t('agent_activity_report.header.last_name'),
          assignedPassportData?.lastname,
          lang,
        )}
        {renderField(
          t('agent_activity_report.header.job'),
          assignedPassportData?.job,
          lang,
        )}
      </View>
    </CommonHeader>
  )
}

export const AlarmHeader = ({
  entity,
  assignedEntity,
}: TAlarmReportProps): JSX.Element => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language

  return (
    <CommonHeader
      customStyle={{
        backgroundColor: commonStyles.alarmBackgroundColor.backgroundColor,
      }}
      lang={lang}
    >
      <View style={commonStyles.column}>
        {renderField(t('alarm_report.header.id'), entity.id, lang)}
        {renderField(
          t('alarm_report.header.creation_date'),
          normalizeArabicComma(
            stripBidi(
              t('alarm_report.logs.date_format', {
                date: new Date(entity.creationDate),
              }),
            ),
          ),
          lang,
        )}
        {renderField(
          t('alarm_report.header.originator_system'),
          entity.originatorSystem,
          lang,
        )}
        <Field2
          i18nKey={'alarm_report.header.assigned'}
          values={getAssignedValues({ assignedEntity, t })}
        />
        {/* {renderField(assigned.title, assigned.value, lang)} */}
      </View>
      <View style={commonStyles.column}>
        {renderField(t('alarm_report.header.status'), entity.status, lang)}
        {renderField(t('alarm_report.header.category'), entity.category, lang)}
        {typeof entity.subCategory === 'string' &&
          entity.subCategory.length > 0 &&
          renderField(
            t('alarm_report.header.subcategory'),
            entity.subCategory,
            lang,
          )}
        {renderField(t('alarm_report.header.severity'), entity.severity, lang)}
        {renderField(
          t('alarm_report.header.certainty'),
          entity.certainty,
          lang,
        )}
      </View>
    </CommonHeader>
  )
}

const getAssignedValues = ({
  assignedEntity,
  t,
}: Pick<TAlarmReportProps, 'assignedEntity'> & { t: TFunction }): Record<
  string,
  unknown
> => {
  if (Loadable.isFailure(assignedEntity))
    return {
      context: 'error',
    }

  const data = assignedEntity.data
  if (data === null)
    return {
      context: 'none',
    }

  switch (data._tag) {
    case 'Group':
      return {
        context: 'group',
        group: {
          ...data,
          '@nameLeaf': getGroupNameLeaf({ group: data, t }),
        },
      }
    case 'Passport':
      return {
        context: 'passport',
        passport: data,
      }
    default:
      return absurd(data)
  }
}

const getGroupNameLeaf = ({
  group,
  t,
}: {
  group: TGroupReference
  t: TFunction
}): string => {
  const { name } = group
  if (name === '/') return t('entities.group.@nameLeaf', getOpts('/'))

  const inclusiveEnd = name.length - (name.endsWith('/') ? 2 : 1)
  const inclusiveStart = name.lastIndexOf('/', inclusiveEnd) + 1

  const leaf = name.substring(inclusiveStart, inclusiveEnd + 1)
  return t('entities.group.@nameLeaf', getOpts(leaf))

  function getOpts(nameLeaf: string): Record<string, unknown> {
    return {
      context: nameLeaf.trim() === '' ? 'notPrintable' : undefined,
      group: {
        ...group,
        '@nameLeaf': nameLeaf,
      },
    }
  }
}
